import { useMemo, useState } from "react";
import { Copy, ScanSearch, Trash2 } from "lucide-react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { useTemplatesStore } from "@/dentlab/src/store/Templates";
import { useServiceCatalogStore } from "@/dentlab/src/store/ServiceCatalog";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { TemplateDeletionDialog } from "@/dentlab/src/components/template-dialogs/template-deletion-dialog.component";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/dentlab/src/components/ui/alert-dialog";
import { SheetHeaderProps } from "../../sheet-provider/sheetRegistry";
import { SheetContentType, useSheetStore } from "../../sheet.store";

interface ArticleSheetActionsProps extends SheetHeaderProps {
	articleId: number;
}

export const ArticleSheetActions: React.FC<ArticleSheetActionsProps> = ({
	sheetKey,
	articleId,
}) => {
	const { articles, deleteArticle, duplicateArticle } = useCentralStore(
		(state) => ({
			articles: state.articles,
			deleteArticle: state.deleteArticle,
			duplicateArticle: state.duplicateArticle,
		})
	);
	const { openSheet, closeSheet } = useSheetStore((state) => ({
		openSheet: state.openSheet,
		closeSheet: state.closeSheet,
	}));
	const { removeItemFromTemplates, checkTemplateUsage } = useTemplatesStore(
		(state) => ({
			removeItemFromTemplates: state.removeItemFromTemplates,
			checkTemplateUsage: state.checkTemplateUsage,
		})
	);

	const article = useMemo(() => {
		for (const articlesForCode of Object.values(articles)) {
			const found = Object.values(articlesForCode).find(
				(a) => a.id === Number(articleId)
			);
			if (found) return found;
		}
		return null;
	}, [articles, articleId]);

	const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
	const [catalogWarningOpen, setCatalogWarningOpen] = useState(false);
	const [catalogFolderNames, setCatalogFolderNames] = useState<string[]>([]);
	const [affectedTemplateIds, setAffectedTemplateIds] = useState<number[]>(
		[]
	);
	const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
	const [duplicateCode, setDuplicateCode] = useState("");
	const [duplicateCodeError, setDuplicateCodeError] = useState<string | null>(
		null
	);

	if (!article) return null;

	const getNextAvailableCode = (baseCode: string): string => {
		const allCodes = new Set(
			Object.values(articles)
				.flatMap((codeGroup) => Object.values(codeGroup))
				.map((a) => a.code_e)
		);

		let suffix = 2;
		let candidate = `${baseCode}-${suffix}`;
		while (allCodes.has(candidate)) {
			suffix++;
			candidate = `${baseCode}-${suffix}`;
		}
		return candidate;
	};

	const isCodeAvailable = (code: string): boolean => {
		for (const codeGroup of Object.values(articles)) {
			for (const a of Object.values(codeGroup)) {
				if (a.code_e === code) return false;
			}
		}
		return true;
	};

	const handleOpenDuplicateDialog = () => {
		if (duplicateDialogOpen) return;
		setDuplicateCode(getNextAvailableCode(article.code_e));
		setDuplicateCodeError(null);
		setDuplicateDialogOpen(true);
	};

	const handleDuplicate = async () => {
		if (!duplicateCode || duplicateCodeError) return;

		const result = await duplicateArticle(article, duplicateCode);
		if (result) {
			setDuplicateDialogOpen(false);
			closeSheet(sheetKey);
			openSheet(SheetContentType.ARTICLE, { articleId: result.id });
			showNotification({
				message: `Sie bearbeiten jetzt den neuen Artikel "${duplicateCode}"`,
				type: "success",
			});
		}
	};

	const handleDelete = async () => {
		const templateIds = await checkTemplateUsage({
			article_id: article.id,
		});
		if (templateIds.length > 0) {
			setAffectedTemplateIds(templateIds);
			setDeletionDialogOpen(true);
			return;
		}

		const folderNames = useServiceCatalogStore
			.getState()
			.checkItemUsage({ article_id: article.id });
		if (folderNames.length > 0) {
			setCatalogFolderNames(folderNames);
			setCatalogWarningOpen(true);
			return;
		}

		await deleteArticle(article.id);
		closeSheet(sheetKey);
	};

	const handleCatalogWarningConfirm = async () => {
		setCatalogWarningOpen(false);
		await deleteArticle(article.id);
		closeSheet(sheetKey);
	};

	const handleRemoveFromTemplatesAndDelete = async () => {
		await removeItemFromTemplates({ article_id: article.id });
		setDeletionDialogOpen(false);
		await deleteArticle(article.id);
		closeSheet(sheetKey);
	};

	return (
		<>
			<div className="flex items-center gap-2">
				<Button
					variant="outline"
					size="sm"
					icon={<ScanSearch />}
					shortcut="r"
					onClick={() =>
						openSheet(SheetContentType.MATERIAL_TRACEABILITY, {
							articleCode: article.code_e,
						})
					}
					data-testid="open-traceability-button"
					aria-label="Materialienrückverfolgung öffnen"
				>
					Rückverfolgung
				</Button>
				<Button
					variant="outline"
					size="sm"
					icon={<Copy />}
					shortcut="d"
					onClick={handleOpenDuplicateDialog}
					data-testid="duplicate-article-button"
					aria-label="Artikel duplizieren"
				>
					Duplizieren
				</Button>
				<Button
					variant="outline"
					size="sm"
					icon={<Trash2 />}
					onClick={handleDelete}
					data-testid="delete-article-button"
					aria-label="Artikel löschen"
				>
					Löschen
				</Button>
			</div>
			<TemplateDeletionDialog
				open={deletionDialogOpen}
				onOpenChange={setDeletionDialogOpen}
				templateIds={affectedTemplateIds}
				itemType="article"
				itemCode={article.code_e}
				canFallbackToDefault={false}
				onCancel={() => setDeletionDialogOpen(false)}
				onFallbackToDefault={() => {}}
				onRemoveFromTemplates={handleRemoveFromTemplatesAndDelete}
			/>
			<AlertDialog
				open={catalogWarningOpen}
				onOpenChange={setCatalogWarningOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Artikel löschen</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div>
								<p className="mb-2">
									Der Artikel &quot;{article.code_e}&quot;
									wird in folgenden Leistungskatalog-Ordnern
									verwendet:
								</p>
								<ul className="list-disc list-inside mb-2 max-h-40 overflow-y-auto">
									{catalogFolderNames.map((name, i) => (
										<li key={i} className="text-sm">
											{name}
										</li>
									))}
								</ul>
								<p>
									Der Artikel wird aus dem Leistungskatalog
									entfernt.
								</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleCatalogWarningConfirm}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							data-testid="confirm-delete-article-from-catalog"
						>
							Löschen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
			<AlertDialog
				open={duplicateDialogOpen}
				onOpenChange={setDuplicateDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Artikel duplizieren</AlertDialogTitle>
						<AlertDialogDescription>
							Geben Sie einen neuen Artikelcode für das Duplikat
							ein.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="py-4">
						<Input
							value={duplicateCode}
							onChange={(e) => {
								const val = e.target.value;
								setDuplicateCode(val);
								setDuplicateCodeError(
									val && !isCodeAvailable(val)
										? "Dieser Artikelcode existiert bereits"
										: null
								);
							}}
							placeholder="Neuer Artikelcode"
							className={
								duplicateCodeError ? "border-destructive" : ""
							}
							autoFocus
							onFocus={(e) => e.target.select()}
							data-testid="duplicate-article-code-input"
							aria-label="Neuer Artikelcode"
						/>
						{duplicateCodeError && (
							<p className="text-sm text-destructive mt-1">
								{duplicateCodeError}
							</p>
						)}
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDuplicate}
							disabled={!duplicateCode || !!duplicateCodeError}
							data-testid="confirm-duplicate-article"
						>
							Duplizieren
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};
